import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CheckCircle, Loader2, Pencil, Play } from 'lucide-react';
import { ImportEditTable } from '@/components/dictionary-import/ImportEditTable';
import { ImportPreview } from '@/components/dictionary-import/ImportPreview';
import { DataElement, ElementVersion, SourceFieldMapping } from '@/entities/all';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Maps Avro/Iceberg source metadata types to Apache Arrow types
const AVRO_TO_ARROW_TYPE_MAP = {
  'string': 'string', 'utf8': 'string', 'fixed': 'binary', 'uuid': 'uuid', 'binary': 'binary', 'bytes': 'binary',
  'int': 'int32', 'integer': 'int32', 'short': 'int16',
  'long': 'int64',
  'float': 'float32', 'double': 'float64', 'decimal': 'decimal128',
  'boolean': 'bool',
  'date': 'date32',
  'time': 'time32',
  'timestamp': 'timestamp', 'timestamptz': 'timestamp', 'timestamp_ntz': 'timestamp',
  'struct': 'struct', 'record': 'struct', 'map': 'map',
  'list': 'list', 'array': 'list',
  // Arrow types pass through
  'null': 'null', 'bool': 'bool',
  'int8': 'int8', 'int16': 'int16', 'int32': 'int32', 'int64': 'int64',
  'uint8': 'uint8', 'uint16': 'uint16', 'uint32': 'uint32', 'uint64': 'uint64',
  'float16': 'float16', 'float32': 'float32', 'float64': 'float64',
  'date32': 'date32', 'time32': 'time32', 'time64': 'time64',
  'decimal128': 'decimal128', 'duration': 'duration',
  'large_utf8': 'large_string', 'large_binary': 'large_binary',
};

function mapDataType(t) {
  return t ? AVRO_TO_ARROW_TYPE_MAP[t.toLowerCase().trim()] || 'string' : 'string';
}

const STAGES = {
  PREVIEW: 'preview',
  EDIT: 'edit',
  IMPORTING: 'importing',
  COMPLETE: 'complete'
};

export default function AddElementsFromFieldsDialog({ open, onOpenChange, unmappedFields = [], dataSourceId, onComplete }) {
  const { user } = useAuth();
  const [stage, setStage] = useState(STAGES.PREVIEW);
  const [editableItems, setEditableItems] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const editTableRef = useRef(null);

  // Generate element records from unmapped fields when dialog opens
  useEffect(() => {
    if (open && unmappedFields.length > 0) {
      const items = unmappedFields.map(field => ({
        canonical_name: field.field_name,
        definition: '',
        data_type: mapDataType(field.data_type),
        status: 'DRAFT',
        version_number: 1,
        business_summary: '',
        processing_rules: [],
        processing_language: 'python',
        reference_list_name: null,
        completeness_weight: 1,
        timeliness_weight: 1,
        confidence_score: 0.95,
        _source_type: field.data_type, // keep original type for reference
        _source_field_name: field.field_name, // preserve original field name for mapping
      }));
      setEditableItems(items);
      setStage(STAGES.PREVIEW);
      setImportResult(null);
      setError(null);
    }
  }, [open, unmappedFields]);

  const handleClose = () => {
    setStage(STAGES.PREVIEW);
    setEditableItems([]);
    setImportResult(null);
    setError(null);
    onOpenChange(false);
  };

  const handleProceedToEdit = () => {
    setStage(STAGES.EDIT);
  };

  const handleBackToPreview = () => {
    setStage(STAGES.PREVIEW);
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setStage(STAGES.IMPORTING);

    const userName = (user?.first_name && user?.last_name)
      ? `${user.first_name} ${user.last_name}`
      : user?.email || 'Import';

    let addedCount = 0;
    const errors = [];
    const total = editableItems.length;
    setImportProgress({ current: 0, total });

    for (let i = 0; i < editableItems.length; i++) {
      const item = editableItems[i];
      setImportProgress({ current: i + 1, total });

      try {
        // Step 1: Create DataElement
        const newElement = await DataElement.create({
          canonical_name: item.canonical_name,
          is_public: false,
          current_version_number: 1,
        });

        // Step 2: Create ElementVersion
        await ElementVersion.create({
          element_id: newElement.id,
          version_number: 1,
          changed_by: userName,
          definition: item.definition || 'No definition provided',
          data_type: item.data_type,
          status: item.status || 'DRAFT',
          confidence_score: item.confidence_score || 0.95,
          creation_method: 'AUTOMATED',
          creation_details: {
            source: 'bulk_unmapped_field_workflow',
            source_field: item.canonical_name,
            data_source_id: parseInt(dataSourceId),
          },
          business_summary: item.business_summary || null,
          completeness_weight: item.completeness_weight ?? 1,
          timeliness_weight: item.timeliness_weight ?? 1,
        });

        // Step 3: Create SourceFieldMapping
        await SourceFieldMapping.create({
          source_id: parseInt(dataSourceId),
          canonical_element_id: newElement.id,
          source_field_name: item._source_field_name || item.canonical_name,
          mapping_confidence: 0.8,
          last_validated: new Date().toISOString().split('T')[0],
        });

        addedCount++;
      } catch (err) {
        errors.push({ canonical_name: item.canonical_name, error: err.message });
      }
    }

    setImportResult({ added: addedCount, errors });
    setStage(STAGES.COMPLETE);
    setImporting(false);

    if (errors.length === 0) {
      toast.success(`Created ${addedCount} data elements with mappings.`);
    } else if (addedCount > 0) {
      toast.warning(`Created ${addedCount} elements, ${errors.length} failed.`);
    } else {
      toast.error(`Failed to create elements: ${errors.length} errors.`);
    }

    if (onComplete) onComplete();
  };

  const preview = {
    toAdd: editableItems,
    toUpdate: [],
    summary: {
      totalRows: editableItems.length,
      toAddCount: editableItems.length,
      toUpdateCount: 0
    }
  };

  const renderPreviewStage = () => (
    <div className="space-y-4">
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          {editableItems.length} new data element{editableItems.length !== 1 ? 's' : ''} will be created from unmapped source fields.
          Each element will also get a source field mapping.
        </AlertDescription>
      </Alert>

      <ImportPreview
        preview={preview}
        errors={[]}
        warnings={[]}
      />
    </div>
  );

  const renderEditStage = () => (
    <div className="space-y-4">
      <Alert className="border-blue-200 bg-blue-50">
        <Pencil className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Review and edit element properties before creating. Click the pencil icon on any row to modify.
        </AlertDescription>
      </Alert>

      <ImportEditTable
        ref={editTableRef}
        items={editableItems}
        onChange={setEditableItems}
      />
    </div>
  );

  const renderImportingStage = () => (
    <div className="space-y-6 py-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center">
          <h3 className="font-semibold text-lg">Creating Elements...</h3>
          <p className="text-sm text-muted-foreground mt-2">
            {importProgress.current} of {importProgress.total} elements
          </p>
        </div>
        <Progress value={(importProgress.current / importProgress.total) * 100} className="w-64" />
      </div>
    </div>
  );

  const renderCompleteStage = () => {
    const added = importResult?.added || 0;
    const errorsList = importResult?.errors || [];

    return (
      <div className="space-y-6 py-4">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className={`h-16 w-16 rounded-full flex items-center justify-center ${errorsList.length > 0 && added === 0 ? 'bg-red-100' : errorsList.length > 0 ? 'bg-yellow-100' : 'bg-green-100'}`}>
            {errorsList.length > 0 && added === 0 ? (
              <AlertCircle className="h-10 w-10 text-red-600" />
            ) : errorsList.length > 0 ? (
              <AlertCircle className="h-10 w-10 text-yellow-600" />
            ) : (
              <CheckCircle className="h-10 w-10 text-green-600" />
            )}
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-xl">
              {errorsList.length > 0 && added === 0 ? 'Creation Failed' : errorsList.length > 0 ? 'Completed with Errors' : 'Elements Created!'}
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              {added > 0 && `${added} element${added !== 1 ? 's' : ''} created with mappings.`}
              {errorsList.length > 0 && ` ${errorsList.length} error${errorsList.length !== 1 ? 's' : ''}.`}
            </p>
          </div>

          <div className={`grid gap-4 w-full max-w-md mt-6 ${errorsList.length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{added}</div>
              <div className="text-sm text-muted-foreground">Created</div>
            </div>
            {errorsList.length > 0 && (
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{errorsList.length}</div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </div>
            )}
          </div>

          {errorsList.length > 0 && (
            <div className="w-full mt-4 max-h-60 overflow-y-auto">
              <div className="text-sm font-medium mb-2">Error Details:</div>
              <div className="space-y-2">
                {errorsList.slice(0, 20).map((err, idx) => (
                  <div key={idx} className="text-sm p-2 bg-red-50 border border-red-200 rounded">
                    <span className="font-medium">{err.canonical_name}:</span>{' '}
                    <span className="text-red-700">{err.error?.substring(0, 100)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (stage) {
      case STAGES.PREVIEW: return renderPreviewStage();
      case STAGES.EDIT: return renderEditStage();
      case STAGES.IMPORTING: return renderImportingStage();
      case STAGES.COMPLETE: return renderCompleteStage();
      default: return null;
    }
  };

  const renderFooter = () => {
    switch (stage) {
      case STAGES.PREVIEW:
        return (
          <>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button variant="outline" onClick={handleProceedToEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit Before Import
            </Button>
            <Button onClick={handleImport} disabled={editableItems.length === 0}>
              <Play className="mr-2 h-4 w-4" />
              Create Elements
            </Button>
          </>
        );
      case STAGES.EDIT:
        return (
          <>
            <Button variant="outline" onClick={handleBackToPreview}>Cancel</Button>
            <Button onClick={() => { setStage(STAGES.PREVIEW); }} disabled={editableItems.length === 0}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Done Editing
            </Button>
          </>
        );
      case STAGES.IMPORTING:
        return null;
      case STAGES.COMPLETE:
        return <Button onClick={handleClose}>Close</Button>;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (stage) {
      case STAGES.PREVIEW: return 'Add Elements from Unmapped Fields';
      case STAGES.EDIT: return 'Edit Properties';
      case STAGES.IMPORTING: return 'Creating Elements';
      case STAGES.COMPLETE: return 'Complete';
      default: return 'Add Elements';
    }
  };

  const getDescription = () => {
    switch (stage) {
      case STAGES.PREVIEW: return 'Create data elements from unmapped source fields. Each element will be mapped to its source field automatically.';
      case STAGES.EDIT: return 'Edit element properties before creating. Click the pencil icon to modify a row.';
      default: return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!w-[95vw] !max-w-[1400px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          {getDescription() && <DialogDescription>{getDescription()}</DialogDescription>}
        </DialogHeader>

        <div className="py-4">
          {renderContent()}
        </div>

        <DialogFooter>
          {renderFooter()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
